/**
 * Calculs et formatage monetaires.
 *
 * Regle fondatrice : tous les montants sont des entiers exprimes en unites
 * mineures de la devise. Voir docs/adr/0005-montants-entiers.md.
 *
 * Ce fichier ne contient que des fonctions pures. Couverture de test exigee : 100 %.
 */
import { DEFAULT_TAX_RATE_PERMILLE, TPS_TAX_RATE_PERMILLE } from './constants/index.js';
/* -------------------------------------------------------------------------- */
/* Devises                                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Exposant de l'unite mineure, par devise.
 * 2 : 1 EUR = 100 centimes. 0 : 1 XOF = 1 XOF, pas de sous-unite.
 */
const CURRENCY_MINOR_EXPONENT = {
    EUR: 2,
    USD: 2,
    GBP: 2,
    CHF: 2,
    CAD: 2,
    MAD: 2,
    TND: 3,
    XOF: 0,
    XAF: 0,
    JPY: 0,
};
const DEFAULT_MINOR_EXPONENT = 2;
export function getMinorExponent(currency) {
    return CURRENCY_MINOR_EXPONENT[currency.toUpperCase()] ?? DEFAULT_MINOR_EXPONENT;
}
/* -------------------------------------------------------------------------- */
/* Conversion et formatage                                                     */
/* -------------------------------------------------------------------------- */
/** 1250 en EUR donne 12.5. Reservee a l'affichage et a Intl. */
export function minorToMajor(amountMinor, currency) {
    return amountMinor / 10 ** getMinorExponent(currency);
}
/**
 * 12.5 en EUR donne 1250. Utilisee a la saisie d'un prix dans l'admin.
 * Arrondit au plus proche : une saisie a trois decimales en EUR est ramenee au centime.
 */
export function majorToMinor(amountMajor, currency) {
    return Math.round(amountMajor * 10 ** getMinorExponent(currency));
}
/**
 * Formate un montant pour l'affichage. Unique point de conversion vers une
 * chaine lisible : ne jamais reimplementer ce formatage dans un composant.
 */
export function formatMoney(amountMinor, currency, locale = 'fr-FR') {
    const exponent = getMinorExponent(currency);
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: exponent,
        maximumFractionDigits: exponent,
    }).format(minorToMajor(amountMinor, currency));
}
/**
 * Analyse une saisie utilisateur ("12,50", "12.50", "1 250") vers des unites
 * mineures. Retourne null si la saisie n'est pas un nombre valide.
 */
export function parseMoney(input, currency) {
    const normalized = input.replace(/\s/g, '').replace(',', '.');
    if (normalized === '' || !/^\d+(\.\d+)?$/.test(normalized))
        return null;
    const value = Number(normalized);
    if (!Number.isFinite(value))
        return null;
    return majorToMinor(value, currency);
}
/* -------------------------------------------------------------------------- */
/* Taux de TVA                                                                 */
/* -------------------------------------------------------------------------- */
/** 100 pour mille donne "10,0 %". */
export function formatTaxRate(taxRatePermille, locale = 'fr-FR') {
    return `${new Intl.NumberFormat(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(taxRatePermille / 10)} %`;
}
/**
 * Taux qu'un regime impose ou propose, quand aucun n'est fourni.
 *
 * Le TPS n'en propose pas : il l'impose. Voir `isTaxRateCoherent`.
 */
export function defaultTaxRatePermille(regime) {
    return regime === 'TPS' ? TPS_TAX_RATE_PERMILLE : DEFAULT_TAX_RATE_PERMILLE;
}
/**
 * Le couple regime / taux est-il tenable ?
 *
 * Un restaurant au TPS facture a 0. Le REEL, lui, ne contraint rien : il admet
 * le taux normal comme un taux reduit ou nul, une vente exoneree restant une
 * vente au regime reel.
 *
 * Cette fonction est la formulation unique de l'invariant de l'ADR 0022. La
 * base le redit en contrainte CHECK, l'API l'applique a la creation et a la
 * modification ; les trois doivent dire la meme chose, et c'est ici qu'elle est
 * ecrite.
 */
export function isTaxRateCoherent(regime, taxRatePermille) {
    return regime !== 'TPS' || taxRatePermille === TPS_TAX_RATE_PERMILLE;
}
export function computeLineTotal(line) {
    return line.unitPriceMinor * line.quantity;
}
/**
 * Calcule les totaux d'une commande.
 *
 * Un seul arrondi, applique au total, jamais ligne par ligne : arrondir chaque
 * ligne ferait deriver la somme de plusieurs centimes sur une grosse commande.
 *
 * Formules : voir docs/03-modele-de-donnees.md, section 5.
 * Invariant garanti : subtotalMinor + taxMinor === totalMinor.
 */
export function computeOrderTotals(lines, taxRatePermille, taxIncluded) {
    const lineTotalsMinor = lines.map(computeLineTotal);
    const sumMinor = lineTotalsMinor.reduce((acc, value) => acc + value, 0);
    if (taxIncluded) {
        // Les prix affiches incluent deja la taxe : on la extrait du total.
        const taxMinor = Math.round((sumMinor * taxRatePermille) / (1000 + taxRatePermille));
        return {
            subtotalMinor: sumMinor - taxMinor,
            taxMinor,
            totalMinor: sumMinor,
            lineTotalsMinor,
        };
    }
    // Les prix affiches sont hors taxes : on ajoute la taxe au total.
    const taxMinor = Math.round((sumMinor * taxRatePermille) / 1000);
    return {
        subtotalMinor: sumMinor,
        taxMinor,
        totalMinor: sumMinor + taxMinor,
        lineTotalsMinor,
    };
}
/* -------------------------------------------------------------------------- */
/* Frais du canal de paiement                                                  */
/* -------------------------------------------------------------------------- */
/**
 * Frais annonces au convive pour un paiement mobile money, en unites mineures.
 *
 * Arrondi au superieur, contrairement aux taxes qui arrondissent au plus
 * proche : annoncer moins que ce que le prestataire prelevera ferait decouvrir
 * la difference au convive au moment de payer. Mieux vaut annoncer un franc de
 * trop qu'un franc de moins.
 *
 * Le resultat est une **estimation d'affichage**. Le montant qui fait foi est
 * celui que le prestataire applique, inscrit ensuite dans `Payment.feeMinor`.
 * Un ecart entre les deux ne bloque jamais un paiement. Voir docs/adr/0018.
 */
export function estimatePaymentFeeMinor(amountMinor, feeRatePermille) {
    if (amountMinor <= 0 || feeRatePermille <= 0)
        return 0;
    return Math.ceil((amountMinor * feeRatePermille) / 1000);
}
/**
 * Ce que le convive verra debite : son addition, plus les frais du canal.
 *
 * Ce total n'est **jamais** le total d'une commande ni d'une facture. Il ne
 * sert qu'a l'annonce avant validation.
 */
export function estimateAmountWithFeeMinor(amountMinor, feeRatePermille) {
    return amountMinor + estimatePaymentFeeMinor(amountMinor, feeRatePermille);
}
/** Panier moyen. Retourne 0 si aucune commande, plutot que NaN. */
export function computeAverageBasketMinor(revenueMinor, orderCount) {
    if (orderCount <= 0)
        return 0;
    return Math.round(revenueMinor / orderCount);
}
//# sourceMappingURL=money.js.map